import { message, Select, UploadProps } from "antd";
import { getCompanyGroups, getIndustryGrpLeaderResult } from "api/search.api";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebounce } from "utils";
import SectionTitleAndDescription from "./SectionTitleAndDescription";
import Label from "components/Common/Label/Label";
import SearchFilterDropdown from "components/Common/SearchableDropdown/SearchFilterDropdown";
import { Title5 } from "components/Common/Typography/Titles";
import { SvgIcons } from "Icon/SvgIcons";
import UploadFile from "components/Common/Input/UploadFile";
import Checkbox from "components/Common/Checkbox/Checkbox";
import { setIncludeSubsidiaries } from "store/slices/searcSlice";
import { RootState } from "store/store";
import Papa from "papaparse";
import { fetchCompanies } from "utils/utils";

interface Company {
  name: string;
}

interface CompanySelectProps {
  title: string;
  dispatchAddAction: any;
  dispatchRemoveAction: any;
  isIncludeCompany: boolean;
  setIsIncludeCompany: any;
  placeholder: string;
  selectedCompanies: Company[];
  dispatchGroupSelect: any;
}

const SelectIncludeCompany: React.FC<CompanySelectProps> = ({
  title,
  dispatchGroupSelect,
  dispatchAddAction,
  dispatchRemoveAction,
  isIncludeCompany,
  setIsIncludeCompany,
  placeholder,
  selectedCompanies,
}) => {
  const dispatch = useDispatch();
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [companiesGroup, setCompaniesGroup] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [allIndustries, setAllIndustries] = useState<{ value: string; label: string }[]>([]);

  const includeSubsidiaries = useSelector((state: RootState) => state.search.include_subsidiaries);

  const getGroups = async () => {
    try {
      setLoading(true);
      const res = await getCompanyGroups(1, page);

      setCompaniesGroup((prevCompaniesGroup) => {
        const newGroups = res.results.filter(
          (newGroup: any) =>
            !prevCompaniesGroup.some((existingGroup) => existingGroup.id === newGroup.id)
        );
        return [...prevCompaniesGroup, ...newGroups];
      });

      setTotalPages(res.total_pages);
      if (page < res.total_pages) {
        setPage((prev) => prev + 1);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const props = {
    name: "file",
    accept: ".csv",
    customRequest: async ({
      file,
      onSuccess,
    }: {
      file: File;
      onSuccess: (data: string[][]) => void;
    }) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target?.result;
        if (typeof text === "string") {
          const result = Papa.parse<string[]>(text, { header: false });

          // Filter out any rows that are empty or contain only spaces
          const filteredData: string[][] = result.data.filter((row: string[]) =>
            row.some((cell) => cell.trim() !== "")
          );

          setCsvData(filteredData);
          onSuccess(filteredData);
        }
      };
      reader.readAsText(file);
    },
    onChange(info: any) {
      if (info.file.status === "done") {
        message.success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
  } as UploadProps;

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight &&
      !loading &&
      page < totalPages
    ) {
      getGroups();
    }
  };

  const handleCompanyGroupChange = (value: string | string[]) => {
    const selectedGroup = companiesGroup.find((group) => group.id === value);
    if (selectedGroup) {
      dispatch(
        dispatchGroupSelect(selectedGroup.companies.map((company: string) => ({ name: company })))
      );
    }
  };

  const handleUploadFile = (file: string[]) => {
    dispatch(
      dispatchGroupSelect(file.map((company: string) => ({ name: company })))
    );
  };

  useEffect(() => {
    handleUploadFile(csvData.flat());
  }, [csvData]);

  const debouncedSearchSkill = useDebounce((query: string) => {
    if (query) {
      fetchCompanies(query, setAllIndustries);
      // fetchIndustryLeaders(query);
    }
  }, 500);

  const handleInputChange = (inputValue: string) => {
    debouncedSearchSkill(inputValue);
  };

  const handleDeselect = (value: string) => {
    dispatch(dispatchRemoveAction(value));
  };

  const handleCompanyChange = (selectedValues: string[]) => {
    const lastSelectedValue = selectedValues[selectedValues.length - 1];

    dispatch(dispatchAddAction(lastSelectedValue));
  };

  useEffect(() => {
    if (companiesGroup.length === 0) {
      getGroups();
    }
  }, []);

  const handleCheck = () => {
    dispatch(setIncludeSubsidiaries(!includeSubsidiaries));
  };

  return (
    <div className="mb-10">
      <SectionTitleAndDescription title={title} isRequiredSection={false} isHelpInfoReq={false} />
      <div className="flex flex-col border-2 rounded-lg border-main-border border-dashed p-7">
        <div className="xl:flex grid xl:gap-12 gap-5 pb-4 items-center">
          <div className="w-full">
            {/* <Label labelTitle={title} isHelpIconRequired tooltipText={"This is a tooltip"} /> */}
            <div className="SelectProject-input">
              <SearchFilterDropdown
                mode="multiple"
                options={allIndustries}
                onInputChange={handleInputChange}
                onChange={handleCompanyChange}
                defaultValue={selectedCompanies.map((company) => company.name)}
                handleDeselect={handleDeselect}
                placeholder={placeholder}
              />
            </div>
          </div>
        </div>
        <div
          className={`flex justify-between items-center overflow-hidden ${!isIncludeCompany ? "max-h-[1000px]" : "max-h-0"
            }`}
        >
          <p
            className="flex-1 text-sm text-primary font-normal mt-2 underline cursor-pointer"
            onClick={() => setIsIncludeCompany(true)}
          >
            Additional Filters
          </p>
          <div className="flex-1">
            <div className="flex space-x-2 mt-2.5 justify-end">
              <Checkbox
                checked={includeSubsidiaries}
                onChange={handleCheck}
                inputId="IncludeCompany"
                inputName="IncludeCompany"
                inputValue="Bike"
                labelFor="IncludeCompany"
                labelTitle="Include company subsidiaries"
              />
            </div>
          </div>
        </div>
        <div
          className={`flex flex-col overflow-hidden transition-all ${isIncludeCompany ? "max-h-[1000px]" : "max-h-0"
            }`}
        >
          <div className="flex gap-2 justify-between">
            <Title5 text="Additional Filters" className="font-semibold mb-0" />
            <div className="flex items-center">
              {SvgIcons.intersectSquare}
              <Select
                placeholder="Company Group"
                variant="borderless"
                onPopupScroll={handlePopupScroll}
                style={{ flex: 1 }}
                onChange={handleCompanyGroupChange}
                options={companiesGroup.map((group) => ({
                  label: group.title,
                  value: group.id,
                }))}
              />
            </div>
          </div>
          <UploadFile {...props} primaryText="Click to upload a list of companies to include" />
          <div className="flex gap-2 justify-between mt-2">
            <Checkbox
              checked={includeSubsidiaries}
              onChange={handleCheck}
              inputId="IncludeCompany"
              inputName="IncludeCompany"
              inputValue="Bike"
              labelFor="IncludeCompany"
              labelTitle="Include company subsidiaries"
            />
            <div className="flex items-center" onClick={() => setIsIncludeCompany(false)}>
              {SvgIcons.primaryCrossSvg}
              <p className="flex-1 text-sm text-primary font-normal cursor-pointer">Close Bulk</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectIncludeCompany;
